import React, { useRef, useState } from 'react';
import { FiSend } from 'react-icons/fi';
import { sendMetaMessage, type MetaReplyChannel, type MetaSendResult } from '../../services/MetaMessagesService';
import { sendWebchatMessage } from '../../services/WebchatService';
import { MetaWindowNotice } from './MetaWindowNotice';
import { useMetaWindow } from './useMetaWindow';
import { INSTAGRAM_MAX_BYTES, newMetaIdempotencyKey, utf8ByteLength } from './metaText';
import './metaConversation.css';

interface Props {
  sectorId: number;
  contactId: number;
  /** 'webchat' = resposta ao visitante do site: sem janela nem limite de bytes. */
  channel: MetaReplyChannel | 'webchat';
  /** Muda quando chega mensagem nova: o prazo da Meta é refeito (a mensagem do cliente reabre a janela). */
  refreshKey: unknown;
  onSent: (result: MetaSendResult, text: string) => void;
}

const LABEL: Record<MetaReplyChannel | 'webchat', string> = { instagram: 'Instagram', messenger: 'Messenger', webchat: 'Site' };
export const WEBCHAT_REPLY_NOTE = 'O visitante vê a resposta se estiver no site ou na próxima visita';

/**
 * Resposta humana pela Meta: faixa do prazo, contador de bytes UTF-8 (1000 no Instagram) e bloqueio com a janela fechada ou o
 * texto acima do limite. A mesma chave de idempotência vale para a nova tentativa do mesmo texto.
 */
export const MetaReplyComposer: React.FC<Props> = ({ sectorId, contactId, channel, refreshKey, onSent }) => {
  const isSite = channel === 'webchat';
  // Site não tem janela da Meta: sem consulta (sectorId nulo desliga o hook).
  const meta = useMetaWindow(isSite ? null : sectorId, contactId, isSite ? 'instagram' : channel, refreshKey);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const keyRef = useRef<string | null>(null);

  const max = isSite ? null : meta.window?.maxBytes ?? (channel === 'instagram' ? INSTAGRAM_MAX_BYTES : null);
  const bytes = utf8ByteLength(text);
  const tooLong = max != null && bytes > max;
  const closed = !isSite && meta.window?.state === 'closed';
  const trimmed = text.trim();
  const canSend = !busy && !closed && !tooLong && trimmed.length > 0;

  const send = async () => {
    if (!canSend) return;
    setBusy(true);
    setError(null);
    keyRef.current ??= newMetaIdempotencyKey();
    try {
      const result: MetaSendResult = isSite
        ? { ...(await sendWebchatMessage({ sectorId, contactId, text: trimmed, idempotencyKey: keyRef.current })), captured: false }
        : await sendMetaMessage({ sectorId, contactId, channel, text: trimmed, idempotencyKey: keyRef.current });
      keyRef.current = null;
      setText('');
      onSent(result, trimmed);
      if (!isSite) void meta.reload();
    } catch (e) {
      const message = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(message?.trim() || 'Não foi possível enviar a mensagem agora.');
      // Recusa por prazo, conta caída ou limite: o prazo pode ter mudado.
      if (!isSite) void meta.reload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="meta-composer">
      {isSite ? <p className="meta-composer-note" role="status">{WEBCHAT_REPLY_NOTE}</p> : <MetaWindowNotice window={meta.window} error={meta.error} />}
      <textarea
        className="meta-composer-input"
        aria-label={`Mensagem para o ${LABEL[channel]}`}
        placeholder={closed ? 'Aguarde o cliente escrever para responder.' : `Responder pelo ${LABEL[channel]}…`}
        value={text}
        disabled={closed}
        onChange={(e) => {
          setText(e.target.value);
          keyRef.current = null; // texto novo é mensagem nova: chave nova
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            void send();
          }
        }}
      />
      <div className="meta-composer-foot">
        {max != null ? (
          <span className={`meta-composer-count${tooLong ? ' meta-composer-count-over' : ''}`}>{`${bytes}/${max} bytes`}</span>
        ) : <span />}
        <button type="button" className="meta-composer-send" onClick={() => void send()} disabled={!canSend}>
          <FiSend size={14} aria-hidden="true" /> {busy ? 'Enviando…' : 'Enviar'}
        </button>
      </div>
      {tooLong && (
        <div className="meta-composer-error" role="alert">
          {`No ${LABEL[channel]} a mensagem tem limite de ${max} bytes; encurte o texto.`}
        </div>
      )}
      {error && !tooLong && <div className="meta-composer-error" role="alert">{error}</div>}
    </div>
  );
};
